import { Button } from "@mui/material";
import { ChevronLeft20Px, ConfirmationModal } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import { setHoldTypeFilters } from "./inventory.slice";
import { useViewType } from "./useViewType";

type InventoryBackButtonProps = {
  isAdjustingBins: boolean;
  isGetBinsLoading: boolean;
  handleFetchAllInventory: () => void;
};

export const InventoryBackButton = ({
  isAdjustingBins,
  isGetBinsLoading,
  handleFetchAllInventory
}: InventoryBackButtonProps) => {
  const { t } = useTranslation();
  const trackedPageName = useMixpanelPageName();
  const { isHoldsPage } = useViewType();
  const { variantId: variantIdParam, binNumber: binNumberParam } = useParams<{
    variantId?: string;
    binNumber?: string;
  }>();
  const dispatch = useAppDispatch();
  const isAutostorePutawayDialogOpen = useAppSelector(
    (state) => state.inventoryNew.isAutostorePutawayDialogOpen
  );
  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);

  if (!(isAdjustingBins || variantIdParam || binNumberParam)) {
    return null;
  }

  const isDisabled = isGetBinsLoading || isAutostorePutawayDialogOpen;

  return (
    <>
      <Button
        sx={{ alignSelf: "flex-start", mb: 1 }}
        variant="subtle"
        onClick={() => {
          if (isAdjustingBins || isGetBinsLoading) {
            setIsRefreshModalOpen(true);
          } else {
            handleFetchAllInventory();
            dispatch(setHoldTypeFilters([]));
          }
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: isHoldsPage
              ? "Return to inventory holds"
              : "Return to all inventory"
          });
        }}
        startIcon={
          <ChevronLeft20Px
            fill="currentColor"
            opacity={isDisabled ? 0.25 : 1}
          />
        }
        style={{ fontWeight: "normal" }}
        disabled={isDisabled}
      >
        {t("back")}
      </Button>
      <ConfirmationModal
        labelledById="return-to-all-inventory-modal-title"
        isOpen={isRefreshModalOpen}
        confirmCb={() => {
          handleFetchAllInventory();
          dispatch(setHoldTypeFilters([]));
          setIsRefreshModalOpen(false);
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Confirm Return to all inventory"
          });
        }}
        closeCb={() => {
          setIsRefreshModalOpen(false);
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Cancel Return to all inventory"
          });
        }}
        modalTitle={
          isHoldsPage
            ? t("return to inventory holds")
            : t("return to all inventory")
        }
        modalText={
          isHoldsPage
            ? t(
                "are you sure you want to close all current bins and return to the inventory holds page?"
              )
            : t(
                "are you sure you want to close all current bins and return to the inventory main page?"
              )
        }
      />
    </>
  );
};
